import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, Copy, Play, RefreshCw, TerminalSquare } from 'lucide-react';
import { api, jsonBody } from './api';
import { Select } from './Select';
import type { Notify } from './types';

type CommandStatus = 'queued' | 'running' | 'completed' | 'failed' | 'expired' | 'cancelled';

type ExecPayload = {
  argv?: string[];
  command?: string;
  shell?: boolean;
  timeout_ms?: number;
};

type ExecResult = {
  exit_code?: number;
  stdout?: string;
  stderr?: string;
  encoding?: string;
  truncated?: boolean;
  duration_ms?: number;
};

type ConsoleCommand = {
  id: string;
  type: string;
  payload: ExecPayload | null;
  status: CommandStatus;
  attempts: number;
  result: ExecResult | null;
  error_code: string;
  expires_at: string | null;
  created_at: string;
  pulled_at: string | null;
  acknowledged_at: string | null;
};

type ConsoleList = { items: ConsoleCommand[]; caps: string[] | null };

const STATUS_LABEL: Record<CommandStatus, string> = {
  queued: '排队中',
  running: '执行中',
  completed: '已完成',
  failed: '失败',
  expired: '已过期',
  cancelled: '已取消',
};

const STATUS_TONE: Record<CommandStatus, string> = {
  queued: 'warn',
  running: 'warn',
  completed: 'ok',
  failed: 'danger',
  expired: 'neutral',
  cancelled: 'neutral',
};

// 默认不过期：离线设备可以一直保留命令，直到它重新上线。
const EXPIRY_OPTIONS = [
  { value: 'never', label: '不过期' },
  { value: '60', label: '1 分钟' },
  { value: '300', label: '5 分钟' },
  { value: '900', label: '15 分钟' },
  { value: '1800', label: '30 分钟' },
  { value: '3600', label: '1 小时' },
];

function formatTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('zh-CN', {
    month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
}

function commandLine(command: ConsoleCommand): string {
  const payload = command.payload ?? {};
  if (payload.command) return payload.command;
  if (payload.argv && payload.argv.length > 0) return payload.argv.join(' ');
  return '（空命令）';
}

function errorText(cause: unknown): string {
  return cause instanceof Error ? cause.message : '操作失败，请重试';
}

/**
 * DeviceConsole renders one device's exec queue.
 *
 * Commands are ordinary device_commands rows delivered by the agent's sync
 * loop, so the panel polls the same history the device pulls. A 2-second
 * refresh keeps the terminal feel without opening a second long-lived stream.
 */
export function DeviceConsole({ deviceId, notify }: { deviceId: string; notify: Notify }) {
  const [items, setItems] = useState<ConsoleCommand[]>([]);
  const [caps, setCaps] = useState<string[] | null>(null);
  const [input, setInput] = useState('');
  const [shell, setShell] = useState(true);
  const [expiry, setExpiry] = useState('never');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [follow, setFollow] = useState(true);
  const [copied, setCopied] = useState('');
  const historyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const mounted = useRef(true);
  const inFlight = useRef(false);
  const copyTimer = useRef<number | undefined>(undefined);

  const load = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const data = await api<ConsoleList>(`/api/devices/${encodeURIComponent(deviceId)}/console/commands?limit=100`);
      if (!mounted.current) return;
      setItems(data.items ?? []);
      setCaps(data.caps ?? null);
      setError('');
    } catch (cause) {
      if (mounted.current) setError(errorText(cause));
    } finally {
      inFlight.current = false;
      if (mounted.current) setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    mounted.current = true;
    setItems([]);
    setCaps(null);
    setExpiry('never');
    setLoading(true);
    setError('');
    setFollow(true);
    void load();

    let disposed = false;
    let timer = 0;
    const tick = () => {
      if (document.visibilityState === 'visible') void load();
      if (!disposed) timer = window.setTimeout(tick, 2000);
    };
    timer = window.setTimeout(tick, 2000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      disposed = true;
      mounted.current = false;
      window.clearTimeout(timer);
      window.clearTimeout(copyTimer.current);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  useEffect(() => {
    const node = historyRef.current;
    if (follow && node) node.scrollTop = node.scrollHeight;
  }, [items, follow]);

  const send = async () => {
    const command = input.trim();
    if (!command || busy) return;
    setBusy(true);
    try {
      await api(`/api/devices/${encodeURIComponent(deviceId)}/console/commands`, {
        method: 'POST',
        body: jsonBody({
          command,
          shell,
          ...(expiry === 'never' ? {} : { expires_in_seconds: Number(expiry) }),
        }),
      });
      setInput('');
      setFollow(true);
      const expiryLabel = EXPIRY_OPTIONS.find((option) => option.value === expiry)?.label ?? '不过期';
      notify('success', expiry === 'never'
        ? '命令已排队，设备下次 sync 时拉取；不会过期'
        : `命令已排队，${expiryLabel}内有效`);
      await load();
      inputRef.current?.focus();
    } catch (cause) {
      notify('error', errorText(cause));
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const copy = async (command: ConsoleCommand) => {
    const result = command.result ?? {};
    const text = result.stdout || result.stderr
      ? [result.stdout, result.stderr].filter(Boolean).join('\n')
      : commandLine(command);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(command.id);
      window.clearTimeout(copyTimer.current);
      copyTimer.current = window.setTimeout(() => setCopied(''), 2000);
    } catch {
      notify('error', '复制失败，请选中文本手动复制');
    }
  };

  const rows = [...items].reverse();
  const execReady = caps === null || caps.includes('exec');

  return (
    <section className="console-panel" aria-label="设备控制台">
      <header className="console-head">
        <div className="console-title">
          <span className="console-title-icon"><TerminalSquare size={17} /></span>
          <div>
            <strong>设备控制台</strong>
            <span>命令走 sync 队列，默认不过期；设备拉取执行后回传结果。</span>
          </div>
        </div>
        <div className="console-head-actions">
          <span className={`console-live${error ? ' error' : ''}`}><i />{error ? '同步失败' : '每 2 秒刷新'}</span>
          <button type="button" className="secondary-button small" disabled={busy} onClick={() => void load()}>
            <RefreshCw size={14} />刷新
          </button>
        </div>
      </header>

      {!execReady && (
        <div className="console-alert">
          <AlertTriangle size={15} />
          <span>设备最近上报的 caps 不含 <code>exec</code>：请用 <code>--allow-exec</code> 启动 agent，否则命令会以 <code>EXEC_NOT_ALLOWED</code> 失败。</span>
        </div>
      )}
      {error && (
        <div className="console-alert danger" role="alert">
          <AlertTriangle size={15} />
          <span>{error}。已保留当前记录。</span>
          <button type="button" className="text-button" onClick={() => void load()}>重试</button>
        </div>
      )}

      <div
        className="console-history"
        ref={historyRef}
        role="region"
        aria-label="命令历史"
        aria-busy={loading}
        onScroll={(event) => {
          const node = event.currentTarget;
          const atBottom = node.scrollHeight - node.scrollTop - node.clientHeight < 48;
          setFollow(atBottom);
        }}
      >
        {loading && rows.length === 0 && (
          <div className="console-empty"><RefreshCw className="spin" size={22} /><strong>加载控制台…</strong></div>
        )}
        {!loading && rows.length === 0 && (
          <div className="console-empty">
            <TerminalSquare size={26} />
            <strong>从第一条命令开始</strong>
            <span>命令会进入设备 sync 队列；设备以 <code>--allow-exec</code> 启动后即可执行并回传输出。</span>
          </div>
        )}
        {rows.map((command) => {
          const result = command.result ?? {};
          const effectiveStatus: CommandStatus = (command.status === 'queued' || command.status === 'running')
            && command.expires_at && new Date(command.expires_at).getTime() < Date.now()
            ? 'expired'
            : command.status;
          const output = typeof result.stdout === 'string' ? result.stdout : '';
          const stderr = typeof result.stderr === 'string' ? result.stderr : '';
          const hasMeta = result.exit_code !== undefined || command.error_code || result.truncated || command.expires_at;
          return (
            <article className={`console-entry ${effectiveStatus}`} key={command.id}>
              <div className="console-entry-head">
                <span className="console-prompt">❯</span>
                <code>{commandLine(command)}</code>
                <span className={`status-pill ${STATUS_TONE[effectiveStatus] ?? 'neutral'}`}>{STATUS_LABEL[effectiveStatus] ?? effectiveStatus}</span>
                <time dateTime={command.created_at}>{formatTime(command.created_at)}</time>
                <button
                  type="button"
                  className="icon-button"
                  title="复制输出"
                  aria-label="复制输出"
                  onClick={() => void copy(command)}
                >
                  {copied === command.id ? <Check size={13} /> : <Copy size={13} />}
                </button>
              </div>
              {effectiveStatus === 'queued' && (
                <p className="console-note">
                  等待设备下一次 sync 拉取；{command.expires_at
                    ? `将在 ${formatTime(command.expires_at)} 过期。`
                    : '不会过期。'}
                </p>
              )}
              {effectiveStatus === 'running' && <p className="console-note">设备已拉取，等待执行结果。</p>}
              {effectiveStatus === 'expired' && (
                <p className="console-note">
                  已超过排队有效期{command.expires_at ? `（${formatTime(command.expires_at)}）` : ''}；设备下次 sync 时不会再执行。
                </p>
              )}
              {output && <pre className="console-output">{output}</pre>}
              {stderr && <pre className="console-output stderr">{stderr}</pre>}
              {hasMeta && (
                <div className="console-entry-meta">
                  {result.exit_code !== undefined && <span>exit {result.exit_code}</span>}
                  {result.duration_ms !== undefined && <span>{result.duration_ms} ms</span>}
                  {result.truncated && <span className="warn">输出已截断</span>}
                  {result.encoding === 'base64' && <span className="warn">base64 输出</span>}
                  {command.expires_at && <span>有效期至 {formatTime(command.expires_at)}</span>}
                  {command.error_code && <span className="danger">{command.error_code}</span>}
                </div>
              )}
            </article>
          );
        })}
      </div>

      {!follow && rows.length > 0 && (
        <button
          type="button"
          className="console-jump"
          onClick={() => {
            setFollow(true);
            historyRef.current?.scrollTo({ top: historyRef.current.scrollHeight, behavior: 'smooth' });
          }}
        >
          回到最新
        </button>
      )}

      <form
        className="console-composer"
        onSubmit={(event) => {
          event.preventDefault();
          void send();
        }}
      >
        <span className="console-prompt">❯</span>
        <textarea
          ref={inputRef}
          rows={2}
          value={input}
          spellCheck={false}
          placeholder="输入命令，Enter 执行，Shift+Enter 换行"
          aria-label="控制台命令"
          disabled={busy}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              void send();
            }
          }}
        />
        <div className="console-composer-actions">
          <div className="console-composer-options">
            <label className="console-shell-toggle" title="关闭后按 argv 拆分，不经过 shell">
              <input type="checkbox" checked={shell} onChange={(event) => setShell(event.target.checked)} disabled={busy} />
              <code>/bin/sh -c</code>
            </label>
            <div className="console-expiry-toggle">
              <span>排队有效期</span>
              <Select
                className="console-expiry-select"
                aria-label="排队有效期"
                value={expiry}
                disabled={busy}
                onChange={(event) => setExpiry(event.target.value)}
              >
                {EXPIRY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </Select>
            </div>
          </div>
          <button type="submit" className="primary-button small" disabled={busy || !input.trim()}>
            <Play size={14} />{busy ? '排队中…' : '执行'}
          </button>
        </div>
      </form>
    </section>
  );
}
